import {
	BankOutlined,
	CalendarOutlined,
	FileAddOutlined,
	SettingOutlined,
	TeamOutlined,
} from "@ant-design/icons"
import { type MenuItemType } from "components/Menu/Menu"
import { BaseLayout } from "components/BaseLayout/BaseLayout"
import { AdminPermission, User } from "interface/user"
import { useFlag } from "modules/flags/flags"
import { ReactNode, useMemo, useRef } from "react"
import { useLocation, useOutlet } from "react-router"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"

export function AdminLayout() {
	const location = useLocation()
	const currentOutlet = useOutlet()
	const { data: user } = useUserRole()
	const previousOutlet = useRef<ReactNode>(null)
	const isModalPath = location.pathname.includes("/m/")
	const {
		adminCareOrganizations,
		adminConfigs,
		adminEnrollments,
		adminUsers,
		adminVisits,
	} = useFlag(
		"adminCareOrganizations",
		"adminConfigs",
		"adminEnrollments",
		"adminUsers",
		"adminVisits",
	)
	const items = useMemo<MenuItemType[]>(() => {
		if (!user) {
			return []
		}
		return [
			...(adminUsers?.enabled &&
			checkPermission(user, { resourcePath: ["users"], actions: ["list"] })
				? [
						{
							key: "/admin/users",
							icon: <TeamOutlined aria-hidden />,
							label: "Users",
						},
					]
				: []),
			...(adminEnrollments?.enabled &&
			checkPermission(user, {
				resourcePath: ["enrollments"],
				actions: ["list"],
			})
				? [
						{
							key: "/admin/enrollments",
							icon: <FileAddOutlined aria-hidden />,
							label: "Enrollments",
						},
					]
				: []),
			...(adminVisits?.enabled &&
			checkPermission(user, { resourcePath: ["visits"], actions: ["list"] })
				? [
						{
							key: "/admin/visits",
							icon: <CalendarOutlined aria-hidden />,
							label: "Visits",
						},
					]
				: []),
			...(adminCareOrganizations?.enabled &&
			checkPermission(user, {
				resourcePath: ["care-organizations"],
				actions: ["list"],
			})
				? [
						{
							key: "/admin/care-organizations",
							icon: <BankOutlined aria-hidden />,
							label: "Care organizations",
						},
					]
				: []),
			...(adminConfigs?.enabled &&
			checkPermission(user, {
				resourcePath: ["global-config"],
				actions: ["list"],
			})
				? [
						{
							key: "/admin/global-config",
							icon: <SettingOutlined aria-hidden />,
							label: "Global configs",
						},
					]
				: []),
		]
	}, [
		adminCareOrganizations?.enabled,
		adminConfigs?.enabled,
		adminEnrollments?.enabled,
		adminUsers?.enabled,
		adminVisits?.enabled,
		user,
	])

	// Persist the previous non-modal outlet
	if (!isModalPath) {
		previousOutlet.current = currentOutlet
	}

	return (
		<BaseLayout className={cn(items.length === 0 && "lg:grid-cols-1")}>
			<BaseLayout.Header username={user?.fullName} items={items} />

			{items.length > 0 && <BaseLayout.SideNav items={items} />}

			<BaseLayout.Main>
				{/* Background Layer: Either the previous page or PatientDetail fallback */}
				{isModalPath ? previousOutlet.current : currentOutlet}

				{/* Foreground Modal Layer: Only when on the screening path */}
			</BaseLayout.Main>
		</BaseLayout>
	)
}

function checkPermission(user: User, permission: AdminPermission) {
	if (!user.adminPermissions) {
		return false
	}
	return user.adminPermissions.some(
		({ resourcePath, actions }) =>
			permission.resourcePath.every(
				(path) => resourcePath.includes(path) || resourcePath.includes("*"),
			) &&
			permission.actions.every(
				(action) => actions.includes(action) || actions.includes("*"),
			),
	)
}
